import type { RewardInfo } from "@hg/activity-utils"
import { useCallback, useEffect, useState } from "react"

import Toast from "@/components/Toast"
import { getDrawInfo, postDraw } from "@/services/core"
import { useRoleInfo } from "@/user/session"

import poster from "@/assets/imgs/recall/poster/k.png"
import StrokeText from "@/components/StrokeText"
import { i18n } from "@/i18n"
import { collectLottery } from "@/utils/event"
import { RECALL_REWARD_MAP } from "../const/reward"
import ResultModal from "./ResultModal"

import styles from "./index.module.scss"

const StrokeTextDraw = i18n
    .getRecall("poster.draw")
    .split("")
    .map((char, index) => (
        <StrokeText
            key={index}
            text={char}
            fontFamily="Georgia-Bold"
            fontSize={48}
            fillColor="#ffffff"
            borderWidth={3}
            borderColor="#564426"
            offsetY={0}
            padding={3}
            baselineAligned
            className={styles.text}
        />
    ))

const StrokeTextLoading = (
    <StrokeText
        text="..."
        fontFamily="Georgia-Bold"
        fontSize={48}
        fillColor="#ffffff"
        borderWidth={3}
        borderColor="#564426"
        offsetY={0}
        padding={3}
        baselineAligned
        className={styles.text}
    />
)

interface LotteryButtonProps {
    loading?: boolean
    disable?: boolean
    remain: number
    onClick: () => void
}

const LotteryButton: React.FC<LotteryButtonProps> = (props) => {
    const { loading, disable, remain, onClick } = props

    const handleClick = useCallback(() => {
        if (!disable) onClick()
    }, [disable, onClick])

    return (
        <div className={styles.lotteryBtn}>
            <button type="button" onClick={handleClick} disabled={disable}>
                {loading ? StrokeTextLoading : StrokeTextDraw}
            </button>
            <div className={styles.remain}>
                <StrokeText
                    text={i18n.getRecall("poster.remain")}
                    fontFamily="Georgia-Bold"
                    fontSize={22}
                    fillColor="#fffbf1"
                    borderWidth={3}
                    borderColor="#614518"
                    offsetY={0}
                    padding={3}
                    baselineAligned
                    className={styles.text}
                />
                <StrokeText
                    text={String(remain).padStart(2, "0")}
                    fontFamily="Georgia-Bold"
                    fontSize={26}
                    fillColor="#fffbf1"
                    borderWidth={3}
                    borderColor="#614518"
                    offsetY={0}
                    padding={3}
                    baselineAligned
                    className={styles.text}
                />
            </div>
        </div>
    )
}

const getRewardInfo = (prizeName: string) => {
    const reward = RECALL_REWARD_MAP[prizeName]
    return reward ? (reward as RewardInfo) : null
}

interface PosterProps {
    refreshKey?: number
}

const Poster: React.FC<PosterProps> = ({ refreshKey }) => {
    const user = useRoleInfo()
    const [prizeName, setPrizeName] = useState<string | null>(null)
    const [prizeType, setPrizeType] = useState<"GOODS" | "GIFT" | "MISS" | null>(null)
    const [open, setOpen] = useState(false)
    const [remain, setRemain] = useState(0)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!user) return
        getDrawInfo().then((res) => {
            if (res.code === 0) {
                setRemain(res.data.availableDrawCountMap[__RECALL_COUPON_CID] ?? 0)
            }
        })
    }, [user, refreshKey])

    const reward = prizeName ? getRewardInfo(prizeName) : null

    const handleDraw = useCallback(async () => {
        if (loading || remain < 1) return
        setLoading(true)
        try {
            const res = await postDraw(__RECALL_COUPON_CID)
            if (res.code === 0) {
                setRemain((prev) => Math.max(prev - 1, 0))
                if (res.data?.prizeType !== "MISS") {
                    setPrizeName(res.data.giftName)
                    setPrizeType(res.data.prizeType)
                    setOpen(true)
                    collectLottery("recall", res.data.prizeType === "GIFT")
                }
            } else {
                Toast.message(res.msg || "draw failed")
            }
        } finally {
            setLoading(false)
        }
    }, [loading, remain])

    return (
        <>
            <div className={styles.poster}>
                <img className={styles.npc} src={poster} alt="" />
                <LotteryButton
                    loading={loading}
                    disable={loading || remain < 1}
                    remain={remain}
                    onClick={handleDraw}
                />
            </div>
            <ResultModal
                open={open}
                onCancel={() => setOpen(false)}
                reward={reward}
                prizeType={prizeType}
                couponCid={__RECALL_COUPON_CID}
            />
        </>
    )
}

export default Poster
